"use client"

import { useShortcut } from "@/hooks/useShortcut"
import { useAppStore } from "@/store"
import { findElementFromPath } from "@/utils/find-element-from-path"
import { getElementUnderCursor } from "@/utils/get-elem-cursor"
import { pathBuilder } from "@/utils/path-builder"
// import { type Comment, type Prisma } from "@preview-kit/database"
import { TooltipProvider } from "@preview-kit/ui/components/tooltip"

import { api } from "@/lib/api"
import { authClient } from "@/lib/auth-client"
import type { Comment } from "@/types/const.types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import { useShallow } from "zustand/shallow"
import CommandListView from "../command-menu/command-list"
import { ActionBar } from "./action-bar"
import { CommentInputPopover } from "./comment-input-popover"
import { CommentMarker } from "./comment-marker"
import InboxSheet from "./inbox-sheet"

// export interface Comment {
// 	id: string
// 	targetElement: PositionData
// 	relativeOffset: {
// 		xPercent: number
// 		yPercent: number
// 	}
// 	content: string
// 	author: string
// 	timestamp: string
// 	avatar: string
// }

// interface PreviewKitWidgetProps {
// 	// commentsOpen: boolean
// 	// setCommentsOpen: (open: boolean) => void
// 	comments?: Comment[]
// 	onAddComment?: (comment: Omit<Comment, "id" | "timestamp">) => void
// }
export interface NavigationState {
	currentView: React.ReactNode
	breadcrumb: string[]
	viewStack: React.ReactNode[]
}

export default function PreviewKitWidget() {
	const queryClient = useQueryClient()

	const {
		commentsOpen,
		setCommentsOpen,
		setIsAnimated,
		setAnimateClick,
		commandOpen,
		setCommandOpen,
		setSearchQuery,
		isCommentMode,
		setIsCommentMode,
		commentInput,
		setCommentInput,
		selectedComment,
		setSelectedComment,
		navigation,
		setNavigation,
	} = useAppStore(
		useShallow((state) => ({
			commentsOpen: state.commentsOpen,
			setAnimateClick: state.setAnimateClick,
			setCommentsOpen: state.setCommentsOpen,
			setIsAnimated: state.setIsAnimated,
			commandOpen: state.commandOpen,
			setCommandOpen: state.setCommandOpen,
			setSearchQuery: state.setSearchQuery,
			isCommentMode: state.isCommentMode,
			setIsCommentMode: state.setIsCommentMode,
			commentInput: state.commentInput,
			setCommentInput: state.setCommentInput,
			selectedComment: state.selectedComment,
			setSelectedComment: state.setSelectedComment,
			navigation: state.navigation,
			setNavigation: state.setNavigation,
		})),
	)
	const session = authClient.useSession()

	const comments = useQuery({
		queryKey: ["comments"],
		enabled: !!window,
		queryFn: async () =>
			await api
				.get(
					`comments/hostname/${window.location.hostname}?pageUrl=${window.location.pathname}`,
				)
				.json<Comment[]>(),
	})

	const useAddComment = useMutation({
		mutationFn: async (newComment: Omit<Comment, "userId">) =>
			await api.post("comments", { json: newComment }).json<Comment>(),
		onMutate: async (newComment) => {
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey: ["comments"] })

			// Snapshot the previous value
			const previousComments = queryClient.getQueryData<Comment[]>(["comments"])
			if (!session.data?.user) throw new Error("User not authenticated")

			// Optimistically update to the new value
			const optimisticComment: Comment = {
				...newComment,
				id: `temp-${Date.now()}`, // Temporary ID that will be replaced
				userId: "temp-user-id", // This will be replaced with actual user ID from server
				createdAt: new Date(),
				updatedAt: new Date(),
				user: {
					...session.data?.user,
					image: session.data?.user?.image!,
					role: "user",
				},
				resolved: false,
			}

			queryClient.setQueryData<Comment[]>(["comments"], (old) => [
				...(old || []),
				optimisticComment,
			])

			// Return a context object with the snapshotted value
			return { previousComments, optimisticComment }
		},
		onSuccess: (serverComment, _variables, context) => {
			// Replace the optimistic comment with the server response
			queryClient.setQueryData<Comment[]>(["comments"], (old) => {
				if (!old) return [serverComment]
				return old.map((comment) =>
					comment.id === context?.optimisticComment.id
						? serverComment
						: comment,
				)
			})
		},
		onError: (_err, _newComment, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			queryClient.setQueryData(["comments"], context?.previousComments)
		},
		onSettled: () => {
			// Always refetch after error or success to ensure we're in sync with server
			queryClient.invalidateQueries({ queryKey: ["comments"] })
		},
	})

	// Trigger animation on mount
	useEffect(() => {
		const timer = setTimeout(() => {
			setIsAnimated(true)
		}, 100)
		return () => clearTimeout(timer)
	}, [setIsAnimated])

	// Handle click outside to close comment input and selected comment
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			// Close comment input if clicking outside (but not inside popover content)
			if (
				commentInput &&
				!(event.target as HTMLElement).closest(".comment-input-container") &&
				!(event.target as HTMLElement).closest("[data-slot='popover-content']")
			) {
				setCommentInput(null)
			}

			// Close selected comment popover if clicking outside
			if (
				selectedComment &&
				!(event.target as HTMLElement).closest(".comment-marker") &&
				!(event.target as HTMLElement).closest("[data-slot='popover-content']")
			) {
				setSelectedComment(null)
			}
		}

		if (commentInput || selectedComment) {
			// document.addEventListener("mousedown", handleClickOutside)
			window.__PREVIEWKIT_PORTAL_CONTAINER__?.addEventListener(
				"mousedown",
				handleClickOutside,
			)
		}

		return () => {
			// document.removeEventListener("mousedown", handleClickOutside)
			window.__PREVIEWKIT_PORTAL_CONTAINER__?.removeEventListener(
				"mousedown",
				handleClickOutside,
			)
		}
	}, [commentInput, selectedComment, setCommentInput, setSelectedComment])

	useShortcut("OPEN_COMMAND", () => {
		setCommandOpen(true)
		setNavigation({
			currentView: <CommandListView />,
			breadcrumb: [],
			viewStack: [],
		})
		setSearchQuery("")
	})

	useShortcut("COMMENT_MODE", () => {
		setIsCommentMode(true)
		setCommandOpen(false)
	})
	useShortcut("ESCAPE", () => {
		if (commandOpen && navigation.viewStack.length === 0) {
			setCommandOpen(false)
			setSearchQuery("")
		} else if (commandOpen && navigation.viewStack.length > 0) {
			setAnimateClick()
			setNavigation({
				currentView: navigation.viewStack[navigation.viewStack.length - 1],
				breadcrumb: navigation.breadcrumb.slice(0, -1),
				viewStack: navigation.viewStack.slice(0, -1),
			})
			setSearchQuery("")
		} else if (isCommentMode) {
			setIsCommentMode(false)
			setCommentInput(null)
			// Announce comment mode exit to screen readers
			const announcement = document.createElement("div")
			announcement.textContent = "Comment mode disabled"
			announcement.setAttribute("aria-live", "polite")
			announcement.style.position = "absolute"
			announcement.style.left = "-10000px"
			document.body.appendChild(announcement)
			setTimeout(() => document.body.removeChild(announcement), 1000)
		} else if (commentInput) {
			setCommentInput(null)
		} else if (selectedComment) {
			setSelectedComment(null)
		}
	})

	// Handle clicking on the page to place comments
	useEffect(() => {
		const handlePageClick = (event: MouseEvent) => {
			if (!isCommentMode) return

			// Don't place comment if clicking on widget or existing comments
			if (
				(event.target as HTMLElement).closest(".inner_widget") ||
				(event.target as HTMLElement).closest(".comment-marker") ||
				(event.target as HTMLElement).closest(".comment-input-container")
			) {
				return
			}

			// Temporarily remove the pointer-events style to find the real element under the cursor
			const style = document.getElementById("comment-mode-styles")
			if (style) {
				style.remove()
			}

			// Find the element that would be under the cursor without pointer-events disabled
			const elementUnderCursor = getElementUnderCursor(
				event.clientX,
				event.clientY,
			) as HTMLElement

			// Re-add the pointer-events style
			if (isCommentMode) {
				const newStyle = document.createElement("style")
				newStyle.id = "comment-mode-styles"
				newStyle.textContent = `
					button:not(.inner_widget button):not(.comment-marker button):not(.comment-input-container button),
					a:not(.inner_widget a):not(.comment-marker a):not(.comment-input-container a),
					input:not(.inner_widget input):not(.comment-marker input):not(.comment-input-container input),
					select:not(.inner_widget select):not(.comment-marker select):not(.comment-input-container select),
					textarea:not(.inner_widget textarea):not(.comment-marker textarea):not(.comment-input-container textarea),
					[role="button"]:not(.inner_widget [role="button"]):not(.comment-marker [role="button"]):not(.comment-input-container [role="button"]),
					[onclick]:not(.inner_widget [onclick]):not(.comment-marker [onclick]):not(.comment-input-container [onclick]) {
						pointer-events: none !important;
					}
				`
				document.head.appendChild(newStyle)
			}

			if (!elementUnderCursor) return

			// Check if we should skip this element
			if (
				elementUnderCursor.closest(".inner_widget") ||
				elementUnderCursor.closest(".comment-marker") ||
				elementUnderCursor.closest(".comment-input-container")
			) {
				return
			}

			// Find the actual interactive element if we clicked on one
			let actualTarget = elementUnderCursor
			const interactiveSelectors = [
				"button",
				"a",
				"input",
				"select",
				"textarea",
				'[role="button"]',
				"[onclick]",
			]

			// Check if the element under cursor or any of its ancestors is an interactive element
			for (const selector of interactiveSelectors) {
				const interactiveElement = elementUnderCursor.closest(selector)
				if (
					interactiveElement &&
					!interactiveElement.closest(".inner_widget") &&
					!interactiveElement.closest(".comment-marker") &&
					!interactiveElement.closest(".comment-input-container")
				) {
					actualTarget = interactiveElement as HTMLElement
					break
				}
			}

			// Create a modified event with the actual target for pathBuilder
			const modifiedEvent = {
				target: actualTarget,
				currentTarget: actualTarget,
				clientX: event.clientX,
				clientY: event.clientY,
			} as unknown as MouseEvent

			const targetElement = pathBuilder(modifiedEvent)

			const clickedElement = findElementFromPath(targetElement.path)

			if (!clickedElement) return

			const rect = clickedElement.getBoundingClientRect()
			const xPercent = (event.clientX - rect.left) / rect.width
			const yPercent = (event.clientY - rect.top) / rect.height

			setCommentInput({
				targetElement,
				relativeOffset: {
					xPercent,
					yPercent,
				},
				content: "",
				isVisible: true,
			})
			setIsCommentMode(false)
		}

		if (isCommentMode) {
			document.addEventListener("click", handlePageClick, true)
			document.body.style.cursor = "url('/comment-icon.svg') 0 31, crosshair"

			// Disable pointer events on interactive elements to allow commenting
			const style = document.createElement("style")
			style.id = "comment-mode-styles"
			style.textContent = `
				button:not(.inner_widget button):not(.comment-marker button):not(.comment-input-container button),
				a:not(.inner_widget a):not(.comment-marker a):not(.comment-input-container a),
				input:not(.inner_widget input):not(.comment-marker input):not(.comment-input-container input),
				select:not(.inner_widget select):not(.comment-marker select):not(.comment-input-container select),
				textarea:not(.inner_widget textarea):not(.comment-marker textarea):not(.comment-input-container textarea),
				[role="button"]:not(.inner_widget [role="button"]):not(.comment-marker [role="button"]):not(.comment-input-container [role="button"]),
				[onclick]:not(.inner_widget [onclick]):not(.comment-marker [onclick]):not(.comment-input-container [onclick]) {
					pointer-events: none !important;
				}
			`
			document.head.appendChild(style)

			return () => {
				document.removeEventListener("click", handlePageClick, true)
				document.body.style.cursor = "default"

				// Clean up styles on unmount
				const style = document.getElementById("comment-mode-styles")
				if (style) {
					style.remove()
				}
			}
		} else {
			document.body.style.cursor = "default"

			// Remove the style when comment mode is disabled
			const style = document.getElementById("comment-mode-styles")
			if (style) {
				style.remove()
			}
		}
	}, [isCommentMode, setCommentInput, setIsCommentMode])

	// Handle saving comments
	const handleSaveComment = async () => {
		if (!commentInput?.content.trim()) return

		const newComment = {
			xPercentOffset: commentInput.relativeOffset.xPercent,
			yPercentOffset: commentInput.relativeOffset.yPercent,
			targetPath: commentInput.targetElement.path,
			targetX: commentInput.targetElement.x,
			targetY: commentInput.targetElement.y,
			targetSelectionRange: commentInput.targetElement.selectionRange,
			content: commentInput.content,
			hostname: window.location.hostname,
			pageUrl: window.location.pathname,
			pageTitle: document.title,
		}

		// Close the comment input immediately for better UX
		// The optimistic update will show the comment right away
		setCommentInput(null)

		try {
			await useAddComment.mutateAsync(newComment)
		} catch (error) {
			console.error("Failed to add comment:", error)
			// The optimistic update will be rolled back automatically by onError
			// You might want to show a toast notification here to inform the user
		}
	}

	const targetElement = commentInput?.targetElement
	const computedPosition = useMemo(() => {
		if (!commentInput || !targetElement) return null

		const el = findElementFromPath(targetElement.path)
		if (!el) return null

		const rect = el.getBoundingClientRect()

		const absoluteX =
			rect.left +
			rect.width * commentInput.relativeOffset.xPercent +
			window.scrollX
		const absoluteY =
			rect.top +
			rect.height * commentInput.relativeOffset.yPercent +
			window.scrollY

		return { x: absoluteX, y: absoluteY }
	}, [commentInput, targetElement])

	return (
		<TooltipProvider>
			<div className="widget-container">
				{/* Comment Mode Indicator */}
				{isCommentMode && (
					<div
						className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg"
						role="status"
						aria-live="polite"
						aria-label="Comment mode active"
					>
						Click anywhere to place a comment
					</div>
				)}
				{/* Existing Comments */}
				{comments?.data?.map((comment) => (
					<CommentMarker
						key={comment.id}
						comment={comment}
						isSelected={selectedComment?.id === comment.id}
						onSelectionChange={setSelectedComment}
					/>
				))}

				{/* Comment Input Modal */}
				<CommentInputPopover
					commentInput={commentInput}
					computedPosition={computedPosition}
					onClose={() => {
						setCommentInput(null)
					}}
					onContentChange={(content) =>
						setCommentInput({
							...commentInput!,
							content,
						})
					}
					onSave={handleSaveComment}
				/>
				{/* Floating Action Bar */}
				<ActionBar />
				<InboxSheet
					commentsOpen={commentsOpen}
					setCommentsOpen={setCommentsOpen}
				/>
			</div>
		</TooltipProvider>
	)
}
